import { useState, useEffect, useRef, useCallback } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Draggable from 'react-draggable';
import { API, Auth } from 'aws-amplify';

// Context and utilities
import { useAppContext } from '../../Lib/contextLib';
import { getVideo, GetUserVideo } from '../../s3Func/s3Download';

// Components
import PopUp from '../../Popup/logoutPopup';
import { CloudfrontVideoFormat } from '../../videos/CloudfrontVideoFormat';
import Learn from '../../Learn/Learn';
import Practice from './Practice';
import SingingTime from '../../SingingTime/SingingTime';
import FollowTheBeatModule7 from '../../FollowTheBeat/FollowTheBeatModule7';
import Celebrate from '../../Celebrate/Celebrate';
import Surprise from '../../SurpriseVideo/surprise';

// Assets
import presentSound from '../../Unit1/Module1/assets/ta_da_gift.mp3';
import loadingGif from '../../loading-gif-png-5.gif';

// Styles
import './Module12World.css';

// ============================================================================
// Constants
// ============================================================================

const AFTER_CELEBRATE_VIDEO_DAY9 = 'e22793d3d19a423af8143ed9a89c/480.m3u8';
const MODULE_NAME = 'MODULE12';
const NAVIGATION_PATH = '/understandfeelings/navigation';
const SPECIAL_DAYS = { DAY_4: 4, DAY_8: 8 };
const COIN_PRIZE_THRESHOLD = 100;

// Step completion states
const STEPS = {
  NOT_STARTED: 'notstarted',
  LEARN: 'learn',
  PRACTICE: 'practice',
  FOLLOW_THE_BEAT: 'followthebeat',
  CELEBRATE: 'celebrate',
  SINGING_TIME: 'singingtime',
  SURPRISE: 'surprise',
};

// Grade types that bypass certain restrictions
const UNRESTRICTED_GRADES = ['Teacher Mode', 'Free Play'];

// ============================================================================
// Utility Functions
// ============================================================================

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isIOSDevice = () => {
  const iosDevices = [
    'iPad Simulator',
    'iPhone Simulator',
    'iPod Simulator',
    'iPad',
    'iPhone',
    'iPod',
  ];
  return (
    iosDevices.includes(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

const isUnrestrictedGrade = (grade) => UNRESTRICTED_GRADES.includes(grade);

// ============================================================================
// Custom Hooks
// ============================================================================

const useWindowDimensions = () => {
  const [dimensions, setDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return dimensions;
};

// ============================================================================
// Main Component
// ============================================================================

const Module12World = () => {
  const { width: clientWidth, height: clientHeight } = useWindowDimensions();
  const { userHasAuthenticated } = useAppContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state
  const { module12 } = useSelector((state) => state.module12, shallowEqual);
  const { login } = useSelector((state) => state.loginReducer, shallowEqual);

  // Refs for tracking initial renders
  const firstUpdate = useRef(true);
  const coinUpdate = useRef(true);

  // ------------------------------------------------------------------------
  // Image URL Generator
  // ------------------------------------------------------------------------
  const generateImageUrl = (baseUrl) => login.cloudFront + baseUrl + login.cookie;

  // ------------------------------------------------------------------------
  // Image Assets
  // ------------------------------------------------------------------------
  const images = {
    // Backgrounds
    background: generateImageUrl('images/Module12Images/Unit3_Mod2BG.png'),
    practiceBackground: generateImageUrl('images/Module2Images/U1M2_DayScene_Landscape.png'),

    // Character
    animal: generateImageUrl('images/Module2Images/Dog.png'),
    dog: generateImageUrl('images/Module2Images/Dog+Still.gif'),
    dirtCloud: generateImageUrl('images/Module2Images/Dirt+cloud.png'),

    // UI Elements
    coinPick: generateImageUrl('images/Images/AwesomeCoin.png'),
    coinBar: generateImageUrl('images/Images/Coin+Counter.png'),
    journeyMap: generateImageUrl('images/Module1Images/bluejourneymap.svg'),
    greenCheck: generateImageUrl('images/Module1Images/GreenCheck-Button.png'),
    present: generateImageUrl('images/Images/Gift.png'),
    keyReward: generateImageUrl('images/Module2Images/Bone3outof3.png'),

    // Buttons
    logoutBtn: generateImageUrl('images/Module1Images/Logout.png'),
    reviewBtn: generateImageUrl('images/Module1Images/Button_Review.png'),
    rewardBtn: generateImageUrl('images/Module1Images/Button_Reward_3x.png'),
    celebrateBtn: generateImageUrl('images/Module1Images/Button_Celebrate.png'),
    learnBtn: generateImageUrl('images/Module1Images/Button_Learn.png'),
    practiceBtn: generateImageUrl('images/Module1Images/Button_Practice.png'),
    ftbBtn: generateImageUrl('images/Images/Button_FTB.png'),
    singingTimeBtn: generateImageUrl('images/Module1Images/Button_SingingTime.png'),

    // Progress bars
    progressBars: [
      generateImageUrl('images/Module2Images/EmptyBone.png'),
      generateImageUrl('images/Module2Images/Bone1outof3.png'),
      generateImageUrl('images/Module2Images/Bone1outof3.png'),
      generateImageUrl('images/Module2Images/Bone2outof3.png'),
      generateImageUrl('images/Module2Images/Bone3outof3.png'),
    ],

    // Rewards
    rewards: {
      SlideRed: generateImageUrl('images/Module2Images/Reward_RedSlide.png'),
      SlideYellow: generateImageUrl('images/Module2Images/Reward_YellowSlide.png'),
      GnomeBlue: generateImageUrl('images/Module7Images/DailyReward_Gnome1.png'),
      GnomeGreen: generateImageUrl('images/Module7Images/DailyReward_Gnome2.png'),
      TireSwing: generateImageUrl('images/Module2Images/Reward_TireSwing.png'),
      SpringToy: generateImageUrl('images/Module2Images/Reward_BikeSpringChair.png'),
      FairyGreen: generateImageUrl('images/Module7Images/DailyReward_Fairy2.png'),
      FairyPurple: generateImageUrl('images/Module7Images/DailyReward_Fairy1.png'),
      MonkeybarsBlue: generateImageUrl('images/Module2Images/Reward_MonkeyBarsBlue.png'),
      MonkeybarsYellow: generateImageUrl('images/Module2Images/Reward_MonkeyBarYellow.png'),
      FrogCrown: generateImageUrl('images/Module7Images/DailyReward_Frog_Crown2.png'),
      FrogVelvet: generateImageUrl('images/Module7Images/DailyReward_Frog_Crown1.png'),
      KitePink: generateImageUrl('images/Module2Images/Reward_KitePurple.png'),
      KitePurple: generateImageUrl('images/Module2Images/Reward_KitePink.png'),
      FlowerPink: generateImageUrl('images/Module7Images/DailyReward_Flower_Pink.png'),
      FlowerBlue: generateImageUrl('images/Module7Images/DailyReward_Flower_Blue.png'),
      FaeDragon: generateImageUrl('images/Module7Images/BigReward_Fae_Dragon.png'),
      FaeHouse: generateImageUrl('images/Module7Images/BigReward_Fae_House.png'),
    },
  };

  // Progress bar arrays for different day types
  const progressBar5Keys = [
    images.progressBars[0],
    images.progressBars[1],
    images.progressBars[2],
    images.progressBars[2],
    images.progressBars[3],
    images.progressBars[4],
  ];
  const progressBar4Keys = images.progressBars;

  // Reward options by day
  const rewardOptionsByDay = {
    1: [images.rewards.SlideRed, images.rewards.SlideYellow],
    2: [images.rewards.GnomeBlue, images.rewards.GnomeGreen],
    3: [images.rewards.SpringToy, images.rewards.TireSwing],
    4: [images.rewards.FairyGreen, images.rewards.FairyPurple],
    5: [images.rewards.MonkeybarsBlue, images.rewards.MonkeybarsYellow],
    6: [images.rewards.FrogCrown, images.rewards.FrogVelvet],
    7: [images.rewards.KitePink, images.rewards.KitePurple],
    8: [images.rewards.FlowerPink, images.rewards.FlowerBlue],
  };

  // Influencers array
  const influencers = [
    login.influencerid,
    login.influencerid1,
    login.influencerid2,
    login.influencerid3,
    login.influencerid4,
  ];

  // Audio for touch interactions
  const touchSound = new Audio(generateImageUrl('media/Module1Videos/touch_button.mp3'));

  // ------------------------------------------------------------------------
  // UI State
  // ------------------------------------------------------------------------
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // Icon visibility state
  const [reviewIconVisible, setReviewIconVisible] = useState(false);
  const [keyIcon, setKeyIcon] = useState(false);
  const [coinIcon, setCoinIcon] = useState(false);
  const [presentIcon, setPresentIcon] = useState(false);

  // Reward selection state
  const [rewardOption1, setRewardOption1] = useState(null);
  const [rewardOption2, setRewardOption2] = useState(null);
  const [displayReward, setDisplayReward] = useState(false);
  const [displayGreenCheck, setDisplayGreenCheck] = useState(false);
  const [dropZone, setDropZone] = useState(false);
  const [displayGreenButton, setDisplayGreenButton] = useState(false);

  // Screen/component visibility state
  const [displayLearn, setDisplayLearn] = useState(false);
  const [displayPractice, setDisplayPractice] = useState(false);
  const [displayDay4Practice, setDisplayDay4Practice] = useState(false);
  const [displaySingingTime, setDisplaySingingTime] = useState(false);
  const [displayCelebrate, setDisplayCelebrate] = useState(false);
  const [displayBody, setDisplayBody] = useState(true);
  const [displayIntroVideo, setDisplayIntroVideo] = useState(false);
  const [displaySurprise, setDisplaySurprise] = useState(false);
  const [displayVoice, setDisplayVoice] = useState(false);
  const [displayFTB, setDisplayFTB] = useState(false);

  // Component content state
  const [learnComponent, setLearnComponent] = useState('');
  const [day4PracticeComponent, setDay4PracticeComponent] = useState('');
  const [singTimeComponent, setSingTimeComponent] = useState('');
  const [celebrateComponent, setCelebrateComponent] = useState('');
  const [introVideo, setIntroVideo] = useState('');
  const [surpriseVideo, setSurpriseVideo] = useState('');

  // Animal/Dog state
  const [showAnimal, setShowAnimal] = useState(false);
  const [animalState, setAnimalState] = useState('');
  const [animalVoice, setAnimalVoice] = useState('');
  const [dogState, setDogState] = useState(images.dog);
  const [dirtState, setDirtState] = useState(false);
  const [playDigging, setPlayDigging] = useState(false);
  const [playDiggingAudio, setPlayDiggingAudio] = useState(false);
  const [playDogCelebrate, setPlayDogCelebrate] = useState(false);

  // Prize state
  const [prize, setPrize] = useState(false);
  const [displayPrize, setDisplayPrize] = useState(false);

  // Animation state
  const [rewardAnimationState, setRewardAnimationState] = useState('');
  const [coinAnimationState, setCoinAnimationState] = useState('bounceIcons');

  // Audio state
  const [touchButton, setTouchButton] = useState(false);
  const [checkSound, setCheckSound] = useState(false);
  const [buttonSound, setButtonSound] = useState(false);
  const [rewardsFound, setRewardsFound] = useState(false);
  const [coinAudio, setCoinAudio] = useState(false);
  const [audioReady, setAudioReady] = useState(false);

  // Tracking state
  const [checkStep, setCheckStep] = useState('');
  const [showReward, setShowReward] = useState(false);

  // Follow The Beat (FTB) assets
  const [ftbMusic, setFTBMusic] = useState('');
  const [ftbInstruction, setFTBInstruction] = useState('');
  const [ftbInstructionGIF, setFTBInstructionGIF] = useState('');
  const [ftbCount, setFTBCount] = useState('');
  const [ftbTutorial1, setFTBTutorial1] = useState('');
  const [ftbTutorial2, setFTBTutorial2] = useState('');
  const [ftbWalkthrough, setFTBWalkthrough] = useState('');

  // GIF and audio assets
  const [amazingJobDog, setAmazingJobDog] = useState('');
  const [answerSpecialMessage, setAnswerSpecialMessage] = useState('');
  const [answerWatch, setAnswerWatch] = useState('');
  const [practiceIntro, setPracticeIntro] = useState('');
  const [learnIntro, setLearnIntro] = useState('');
  const [celebrateIntro, setCelebrateIntro] = useState('');
  const [followTheBeatIntro, setFollowTheBeatIntro] = useState('');
  const [animalClose, setAnimalClose] = useState('');
  const [animalCloseAudio, setAnimalCloseAudio] = useState('');
  const [dogDig, setDogDig] = useState('');

  // ------------------------------------------------------------------------
  // Audio Helper
  // ------------------------------------------------------------------------

  const playButtonAudio = useCallback(() => {
    sleep(700).then(() => {
      setButtonSound(true);
    });
  }, []);

  // ------------------------------------------------------------------------
  // Asset Loading
  // ------------------------------------------------------------------------

  const loadGifAssets = useCallback(() => {
    // Common GIF assets
    setAmazingJobDog(getVideo('appfiles/answer/answer_amazing_job.gif', login));
    setDogDig(getVideo('appfiles/listening/dog_dig.gif', login));
    setCelebrateIntro(getVideo('appfiles/answer/answer_celebrate_intro.gif', login));
    setLearnIntro(getVideo('appfiles/answer/answer_learn_intro.gif', login));
    setPracticeIntro(getVideo('appfiles/answer/answer_practice_intro.gif', login));
    setAnswerSpecialMessage(getVideo('appfiles/answer/answer_practice_special_message.gif', login));
    setAnswerWatch(getVideo('appfiles/answer/answer_practice_watch_conversation.gif', login));
    setFollowTheBeatIntro(getVideo('appfiles/answer/answer_ftb_intro.gif', login));
    setAnimalClose(getVideo('appfiles/answer/answer_day_finished.gif', login));
    setFTBInstructionGIF(getVideo('appfiles/answer/answer_ftb_instructions.gif', login));
    setFTBCount(getVideo('appfiles/answer/countingDog.gif', login));

    // Audio assets
    setAnimalCloseAudio(getVideo('appfiles/understand_feelings/understand_feelings_day_finished.mp3', login));
    setFTBInstruction(getVideo('appfiles/understand_feelings/understand_feelings_ftb_instructions.mp3', login));
    setFTBMusic(getVideo('appfiles/answer/answer_ftb_audio.m4a', login));

    // Tutorial assets (only for first-time users)
    if (!module12.unlockedDays.day2) {
      setFTBTutorial1(getVideo('appfiles/greet/ftb_walkthrough_touch_circle.mp3', login));
      setFTBTutorial2(getVideo('appfiles/understand_feelings/ftb_walkthrough_sway.mp3', login));
      setFTBWalkthrough(getVideo('appfiles/answer/ftb_walkthrough_audio.mp3', login));
    }
  }, [login, module12.unlockedDays.day2]);

  useEffect(() => {
    loadGifAssets();
  }, [loadGifAssets]);

  // ------------------------------------------------------------------------
  // Animation Handlers
  // ------------------------------------------------------------------------

  const coinAnimation = () => {
    setCoinAnimationState('coinAnimation');
    setCoinAudio(true);
  };

  // ------------------------------------------------------------------------
  // Game State Management
  // ------------------------------------------------------------------------

  const createGameDetails = useCallback(
    (flag, reward, coin) => {
      const gameDetail = { ...module12.game_details };

      if (flag) {
        const flagEntry = { flag, lastUpdated: new Date().toJSON() };
        gameDetail.moduleFlags.push(flagEntry);
      }

      if (coin >= 0) {
        gameDetail.tokens_earned = coin;
      }

      if (reward) {
        const dailyRewards = Object.entries(module12.rewards)
          .filter(([, rewardData]) => rewardData.unlocked)
          .map(([key, rewardData]) => ({
            type: key,
            x_multiplier: rewardData.x,
            y_multiplier: rewardData.y,
          }));
        gameDetail.dailyRewards = dailyRewards;
      }

      dispatch({ type: 'MODULE12_SET_GameDetials', payload: gameDetail });
    },
    [dispatch, module12.game_details, module12.rewards]
  );

  // ------------------------------------------------------------------------
  // API Calls
  // ------------------------------------------------------------------------

  const updateProgress = async () => {
    const payload = {
      body: {
        id: login.studentid,
        module: 'UnderstandFeelings',
        day_completed: module12.currentday,
        time_completed: new Date().toDateString(),
      },
    };

    try {
      await API.put('TIR', '/v3/student/progress', payload);
    } catch (error) {
      // Silent fail - progress update is non-critical
    }
  };

  const updateDB = async () => {
    const modulePayload = {
      body: {
        id: module12.moduleID,
        day_completed: module12.day_completed,
        step_completed: module12.stepCompleted,
        game_details: JSON.stringify(module12.game_details),
      },
    };

    try {
      await API.put('TIR', '/v3/student/module', modulePayload);

      if (displayGreenButton) {
        setAnimalVoice(null);
        setShowAnimal(true);
        setPlayDogCelebrate(true);
        setAnimalState(animalClose);
        setDisplayGreenButton(false);
        setDisplayReward(false);
      }
    } catch (error) {
      const criticalSteps = [STEPS.NOT_STARTED, STEPS.LEARN, STEPS.PRACTICE, STEPS.CELEBRATE, STEPS.FOLLOW_THE_BEAT];
      if (criticalSteps.includes(module12.stepCompleted)) {
        window.alert('Connection is lost. Please login again.');
        dispatch({ type: 'CLEAR' });
        Auth.signOut();
        navigate('/');
      }
    }

    // Update current module for student
    const studentPayload = {
      body: {
        id: login.studentid,
        current_module: 'UnderstandFeelings',
      },
    };

    try {
      await API.put('TIR', '/v3/student', studentPayload);
    } catch (error) {
      // Silent fail - student update is non-critical
    }
  };

  // ------------------------------------------------------------------------
  // Effects for State Synchronization
  // ------------------------------------------------------------------------

  // Sync step completion to database
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    if (!isUnrestrictedGrade(login.grade)) {
      updateDB();
    }
  }, [module12.stepCompleted]);

  // Handle section completion
  useEffect(() => {
    const shouldUpdateProgress =
      (module12.reviewIcon || login.grade === 'Teacher Mode') &&
      module12.sectionCompleted === 4;

    if (shouldUpdateProgress) {
      updateProgress();
      module12.sectionCompleted = 0;
    }
  }, [module12.sectionCompleted]);

  // Handle coin updates and prize eligibility
  useEffect(() => {
    if (module12.coins >= COIN_PRIZE_THRESHOLD) {
      setPrize(true);
    }

    if (coinUpdate.current) {
      coinUpdate.current = false;
      return;
    }

    createGameDetails(false, false, module12.coins);
    updateDB();
  }, [module12.coins]);

  // ------------------------------------------------------------------------
  // UI Handlers
  // ------------------------------------------------------------------------

  const togglePopup = () => {
    setIsPopupOpen((prev) => !prev);
  };

  // Handle progress key updates based on step completion
  useEffect(() => {
    const isDayCompleted = module12.currentday <= module12.day_completed;
    const hasUnrestrictedAccess = isUnrestrictedGrade(login.grade);

    if (isDayCompleted || hasUnrestrictedAccess) {
      // Day is completed or user has unrestricted access
      if (hasUnrestrictedAccess) {
        setShowReward(true);
      }

      dispatch({ type: 'MODULE12_TOGGLE_REVIEW_ICON', payload: true });

      const isSpecialDay = module12.currentday === SPECIAL_DAYS.DAY_4 || module12.currentday === SPECIAL_DAYS.DAY_8;
      dispatch({ type: 'MODULE12_SET_KEY', payload: isSpecialDay ? 5 : 4 });
    } else {
      // Day is in progress
      if (module12.stepCompleted === checkStep) {
        setCoinIcon(true);
      }

      dispatch({ type: 'MODULE12_TOGGLE_REVIEW_ICON', payload: false });

      const isDay4Start = module12.currentday === SPECIAL_DAYS.DAY_4 && module12.day_completed === 3;

      if (isDay4Start) {
        // Day 4 has a different step sequence (includes singing time first)
        const day4KeyMap = {
          [STEPS.NOT_STARTED]: 0,
          [STEPS.SINGING_TIME]: 1,
          [STEPS.LEARN]: 2,
          [STEPS.PRACTICE]: 3,
          [STEPS.FOLLOW_THE_BEAT]: 4,
          [STEPS.CELEBRATE]: 5,
        };

        const keyValue = day4KeyMap[module12.stepCompleted];
        if (keyValue !== undefined) {
          dispatch({ type: 'MODULE12_SET_KEY', payload: keyValue });
          if (module12.stepCompleted === STEPS.CELEBRATE) {
            setKeyIcon(true);
          }
        }
      } else {
        // Standard day progression
        const standardKeyMap = {
          [STEPS.NOT_STARTED]: 0,
          [STEPS.LEARN]: 1,
          [STEPS.PRACTICE]: 2,
          [STEPS.FOLLOW_THE_BEAT]: 3,
          [STEPS.CELEBRATE]: 4,
          [STEPS.SURPRISE]: 5,
        };

        const keyValue = standardKeyMap[module12.stepCompleted];
        if (keyValue !== undefined) {
          dispatch({ type: 'MODULE12_SET_KEY', payload: keyValue });

          // Handle special cases for celebrate step
          if (module12.stepCompleted === STEPS.CELEBRATE) {
            const isDay8Surprise = module12.currentday === SPECIAL_DAYS.DAY_8 && module12.day_completed === 7;
            if (isDay8Surprise) {
              setPresentIcon(true);
            } else {
              setKeyIcon(true);
            }
          } else if (module12.stepCompleted === STEPS.SURPRISE) {
            setKeyIcon(true);
          }
        }
      }

      playButtonAudio();
    }
  }, [module12.stepCompleted]);

  // ------------------------------------------------------------------------
  // Reward Selection Handlers
  // ------------------------------------------------------------------------

  const handleRewardSelection = (selectedOption) => {
    const isOption1 = selectedOption === 1;
    const day = module12.currentday;

    // Update Redux state
    dispatch({ type: `MODULE12_SET_REWARD${day}1`, payload: isOption1 });
    dispatch({ type: `MODULE12_SET_REWARD${day}2`, payload: !isOption1 });

    // Update visual selection feedback
    document.getElementById('rewardItemLeft2').style.borderColor = isOption1 ? 'yellow' : 'white';
    document.getElementById('rewardItemRight2').style.borderColor = isOption1 ? 'white' : 'yellow';

    setTouchButton(true);
    setDisplayGreenCheck(true);
  };

  const handleRewardConfirmation = () => {
    document.getElementById('rewardList').style.zIndex = 1;
    setCheckSound(true);
    setDropZone(true);
    setDisplayGreenCheck(false);
    setDisplayGreenButton(true);
  };

  const handleDayCompletion = () => {
    setCheckSound(true);
    setDropZone(false);
    updateProgress();

    sleep(500).then(() => {
      createGameDetails(false, true);

      if (isUnrestrictedGrade(login.grade)) {
        updateDB();
      }

      const nextDay = module12.currentday + 1;
      const completedDay = module12.currentday === SPECIAL_DAYS.DAY_8 ? 9 : module12.currentday;

      dispatch({ type: `MODULE12_SET_DAY_UNLOCK${nextDay}`, payload: true });
      dispatch({ type: 'MODULE12_SET_dayCompleted', payload: completedDay });
      dispatch({ type: 'MODULE12_SET_stepCompleted', payload: STEPS.NOT_STARTED });
    });
  };

  const renderRewardSelection = () => (
    <>
      <div className="rewards" id="rewardList">
        <div className="circle" id="rewardItemLeft2">
          <img
            src={rewardOption1}
            alt="Reward option 1"
            onClick={() => handleRewardSelection(1)}
          />
        </div>
        <div className="circle" id="rewardItemRight2">
          <img
            src={rewardOption2}
            alt="Reward option 2"
            onClick={() => handleRewardSelection(2)}
          />
        </div>
      </div>

      <div className="greenCheckDiv">
        {displayGreenCheck && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Confirm selection"
            onClick={handleRewardConfirmation}
          />
        )}
      </div>

      {dropZone && (
        <div className="area">
          <div id="dropZone">Drag and drop your item</div>
        </div>
      )}

      <div className="greenCheckDiv">
        {displayGreenButton && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Complete day"
            onClick={handleDayCompletion}
          />
        )}
      </div>
    </>
  );

  // ------------------------------------------------------------------------
  // Prize Selection Handlers
  // ------------------------------------------------------------------------

  const handlePrizeSelection = (selectedOption) => {
    const isOption1 = selectedOption === 1;

    dispatch({ type: 'MODULE12_SET_PRIZE1', payload: isOption1 });
    dispatch({ type: 'MODULE12_SET_PRIZE2', payload: !isOption1 });

    document.getElementById('rewardItemLeft').style.borderColor = isOption1 ? 'yellow' : 'white';
    document.getElementById('rewardItemRight').style.borderColor = isOption1 ? 'white' : 'yellow';

    setTouchButton(true);
    setDisplayGreenCheck(true);
  };

  const handlePrizeConfirmation = () => {
    setDisplayPrize(false);
    setCheckSound(true);
    setDropZone(false);

    sleep(500).then(() => {
      setDisplayGreenButton(false);
      createGameDetails(false, true);
      dispatch({ type: 'MODULE12_SET_COIN', payload: module12.coins - COIN_PRIZE_THRESHOLD });
    });
  };

  const renderPrizeSelection = () => (
    <>
      <div className="rewards">
        <div className="circle" id="rewardItemLeft">
          <img
            src={images.rewards.FaeDragon}
            alt="Fae Dragon prize"
            onClick={() => handlePrizeSelection(1)}
          />
        </div>
        <div className="circle" id="rewardItemRight">
          <img
            src={images.rewards.FaeHouse}
            alt="Fae House prize"
            onClick={() => handlePrizeSelection(2)}
          />
        </div>
      </div>

      <div className="greenCheckDiv">
        {displayGreenCheck && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Confirm selection"
            onClick={() => {
              setCheckSound(true);
              setDropZone(true);
              setDisplayGreenCheck(false);
              setDisplayGreenButton(true);
            }}
          />
        )}
      </div>

      {dropZone && (
        <div className="area">
          <div id="dropZone">Drag and drop your item</div>
        </div>
      )}

      <div className="greenCheckDiv">
        {displayGreenButton && (
          <img
            className="greenCheck"
            src={images.greenCheck}
            alt="Confirm prize"
            onClick={handlePrizeConfirmation}
          />
        )}
      </div>
    </>
  );

  // Set reward options based on current day
  useEffect(() => {
    const options = rewardOptionsByDay[module12.currentday];

    if (options) {
      setRewardOption1(options[0]);
      setRewardOption2(options[1]);
    } else {
      setDisplayGreenButton(true);
    }
  }, [keyIcon, module12.currentday]);

  // ------------------------------------------------------------------------
  // Review Mode Rendering
  // ------------------------------------------------------------------------

  const renderReviewButtons = () => {
    const isDay4 = module12.currentday === SPECIAL_DAYS.DAY_4;

    if (isDay4) {
      // Day 4 includes singing time button
      return (
        <div className="world2reviewContainer">
          <img
            className="reviewBtns41"
            src={images.singingTimeBtn}
            alt="singing button"
            onClick={displaySinging}
          />
          <img
            className="reviewBtns42"
            src={images.learnBtn}
            alt="learn button"
            onClick={openLearn}
          />
          <img
            className="reviewBtns43"
            src={images.practiceBtn}
            alt="practice button"
            onClick={openPractice}
          />
          <img
            className="reviewBtns44"
            src={images.ftbBtn}
            alt="ftb button"
            onClick={openFTB}
          />
          <img
            className="reviewBtns45"
            src={images.celebrateBtn}
            alt="celebrate button"
            onClick={openCelebrate}
          />
        </div>
      );
    }

    // Standard review buttons
    return (
      <div className="world2reviewContainer">
        <img
          className="reviewBtns1"
          src={images.learnBtn}
          alt="learn button"
          onClick={openLearn}
        />
        <img
          className="reviewBtns2"
          src={images.practiceBtn}
          alt="practice button"
          onClick={openPractice}
        />
        <img
          className="reviewBtns3"
          src={images.ftbBtn}
          alt="ftb button"
          onClick={openFTB}
        />
        <img
          className="reviewBtns4"
          src={images.celebrateBtn}
          alt="celebrate button"
          onClick={openCelebrate}
        />
      </div>
    );
  };

  // ------------------------------------------------------------------------
  // Video and Activity Handlers
  // ------------------------------------------------------------------------

  const handleIntroVideoEnd = () => {
    setDisplayIntroVideo(false);
    setDisplayBody(true);

    if (module12.currentday === SPECIAL_DAYS.DAY_4) {
      dispatch({ type: 'MODULE12_SET_KEY', payload: 1 });
      playButtonAudio();
    }

    if (module12.day_completed === 9) {
      navigate(NAVIGATION_PATH);
    }
  };

  const renderIntroduction = () => (
    <div>
      <CloudfrontVideoFormat
        source={introVideo}
        back={NAVIGATION_PATH}
        background={images.background}
        token={login.cookie}
        ended={handleIntroVideoEnd}
      />
    </div>
  );

  // Learn video configuration by day
  const learnVideosByDay = {
    1: { video: '9da475cb999904511af860fe47bb/480.m3u8', closingVideo: 'Module12/Learn_close_day1.mp4' },
    2: { video: '87807c7902d3919220b7c0c7f905/480.m3u8', closingVideo: '8d7c4ecf7fbb9c63849fc97f915f/480.m3u8' },
    3: { video: '6270ac19a130999f3fc659a705bd/480.m3u8', closingVideo: '4f71961a6a1183ccf95ce4425df3/480.m3u8' },
  };

  const openLearn = async () => {
    const song = getVideo('appfiles/understand_feelings/understand_feelings_demo.m4a', login);
    const video = getVideo('appfiles/understand_feelings/learn_video.mp4', login);
    const dayConfig = learnVideosByDay[module12.currentday];

    if (dayConfig) {
      // Days 1-3: Use pre-configured videos
      setLearnComponent(
        <Learn
          video={dayConfig.video}
          songVideo={video}
          closingVideo={dayConfig.closingVideo}
          destiny="/understandfeelings"
          song={song}
          back="/understandfeelings"
          background={images.background}
          setDisplayLearn={setDisplayLearn}
          step_completed="MODULE12_SET_stepCompleted"
          module={module12}
        />
      );
    } else {
      // Days 4+: Load user-generated content
      setShowAnimal(true);
      setAnimalState(learnIntro);
      setAnimalVoice(getVideo('appfiles/understand_feelings/understand_feelings_learn_intro.mp3', login));

      try {
        const userSongUrl = await GetUserVideo(`userfiles/${module12.StudentID}/understand_feelings_play_day_music.m4a`);
        const response = await fetch(userSongUrl);
        const influencerVideo = await GetUserVideo(
          `influencers/${influencers[module12.currentday - 4]}/answer_affirmation.mp4`
        );

        const hasUserSong = response.status === 200;

        setLearnComponent(
          <Learn
            songVideo={video}
            destiny="/understandfeelings"
            song={hasUserSong ? userSongUrl : song}
            back="/understandfeelings"
            background={images.background}
            setDisplayLearn={setDisplayLearn}
            step_completed="MODULE12_SET_stepCompleted"
            module={module12}
            instrumental={hasUserSong ? getVideo('appfiles/understand_feelings/understand_feelings_sing_along.m4a', login) : undefined}
            influencerVideo={influencerVideo}
          />
        );
      } catch (error) {
        console.error('Error loading learn content:', error);
      }
    }

    setDisplayLearn(true);
  };

  const openPractice = () => {
    setAnimalVoice(getVideo('appfiles/understand_feelings/understand_feelings_practice_intro.mp3', login));
    setAnimalState(practiceIntro);
    setShowAnimal(true);
    setDisplayPractice(true);
  };

  const openFTB = () => {
    // Days 4+ show intro animation
    if (module12.currentday > 3) {
      setShowAnimal(true);
      setAnimalState(followTheBeatIntro);
      setAnimalVoice(getVideo('appfiles/answer/answer_ftb_intro.mp3', login));
    }

    setDisplayFTB(true);
  };

  // Celebrate video configuration by day
  const celebrateVideosByDay = {
    1: '4a642a866fb4ff2b54e9c7c024a4/480.m3u8',
    2: 'ca5f564d9daff7adbc2cb0d8bfbf/480.m3u8',
    3: 'a947c274cfd75b2aaf55b4782815/480.m3u8',
  };

  const openCelebrate = async () => {
    const celebrateVideo = celebrateVideosByDay[module12.currentday];

    if (celebrateVideo) {
      // Days 1-3: Simple video celebration
      setCelebrateComponent(
        <Celebrate
          module={module12}
          celebrateVideo={celebrateVideo}
          background={images.background}
          setDisplayCelebrate={setDisplayCelebrate}
          moduleNum={MODULE_NAME}
        />
      );
    } else {
      // Days 4+: Interactive celebration with recording
      setShowAnimal(true);
      setAnimalVoice(getVideo('appfiles/answer/answer_celebrate_intro.mp3', login));
      setAnimalState(celebrateIntro);

      const voice1 = getVideo('appfiles/answer/answer_celebrate_record_amazing.mp3', login);
      const voice2 = getVideo('appfiles/answer/answer_celebrate_say_thanks.mp3', login);
      const voice3 = getVideo('appfiles/answer/answer_amazing_job.mp3', login);
      const animalGIF = getVideo('appfiles/understand_feelings/understand_feelings_joyful.gif', login);
      const animalVoiceClip = getVideo('appfiles/understand_feelings/understand_feelings_joyful_jingle_intro.mp3', login);

      try {
        const influencerVideo = await GetUserVideo(
          `influencers/${influencers[module12.currentday - 4]}/answer_affirmation.mp4`
        );

        setCelebrateComponent(
          <Celebrate
            world3={true}
            module={module12}
            background={images.background}
            voice1={voice1}
            voice2={voice2}
            voice3={voice3}
            animalGIF={animalGIF}
            animalVoice={animalVoiceClip}
            text='Record yourself in an excited voice and say, "I am amazing!"'
            name={`understand_feelings_celebration_surprise_${module12.currentday + 1}.mp4`}
            influencerVideo={influencerVideo}
            animation={amazingJobDog}
            animal={images.animal}
            setDisplayCelebrate={setDisplayCelebrate}
            moduleNum={MODULE_NAME}
          />
        );
      } catch (error) {
        console.error('Error loading celebrate content:', error);
      }
    }

    setDisplayCelebrate(true);
  };

  const displaySinging = () => {
    const introVideoUrl = 'ae95c670e92a45c950c68a33b2f1/480.m3u8';
    const noteVideo = getVideo('appfiles/understand_feelings/understand_feelings_student_notes.mp4', login);
    const singingMusic = getVideo('appfiles/understand_feelings/understand_feelings_sing_along.m4a', login);
    const demoMusic = getVideo('appfiles/understand_feelings/understand_feelings_demo.m4a', login);
    const instrumental = getVideo('appfiles/understand_feelings/understand_feelings_instrumental.m4a', login);

    setSingTimeComponent(
      <SingingTime
        introVideo={introVideoUrl}
        demoMusic={demoMusic}
        singingVideo={noteVideo}
        background={images.background}
        singingMusic={singingMusic}
        setDisplaySingingTime={setDisplaySingingTime}
        module={module12}
        moduleNum={MODULE_NAME}
        name="understand_feelings"
        instrumental={instrumental}
        instrumentalURL="appfiles/understand_feelings/understand_feelings_sing_along.m4a"
      />
    );
    setDisplaySingingTime(true);
  };

  // ------------------------------------------------------------------------
  // Surprise Video Handling
  // ------------------------------------------------------------------------

  const getSurpriseSlides = async () => {
    // Configuration for surprise video slides
    const slideConfigs = [
      { video: `influencers/${login.influencerid}/answer_affirmation.mp4` },
      { video: `userfiles/${module12.StudentID}/understand_feelings_celebration_surprise_5.mp4` },
      { video: `influencers/${login.influencerid1}/answer_affirmation.mp4` },
      { video: `userfiles/${module12.StudentID}/understand_feelings_celebration_surprise_7.mp4` },
      { video: `influencers/${login.influencerid2}/answer_video_message.mp4` },
      { video: `influencers/${login.influencerid3}/answer_affirmation.mp4` },
      { video: `userfiles/${module12.StudentID}/understand_feelings_celebration_surprise_9.mp4` },
      { video: `influencers/${login.influencerid4}/answer_affirmation.mp4` },
    ];

    // Load all video URLs
    const loadedSlides = await Promise.all(
      slideConfigs.map(async (slide) => ({
        video: await GetUserVideo(slide.video, login),
      }))
    );

    return loadedSlides;
  };

  const playSurpriseVideo = async () => {
    const slides = await getSurpriseSlides();

    setSurpriseVideo(
      <Surprise
        background={images.background}
        music={getVideo('appfiles/understand_feelings/understand_feelings_instrumental.m4a', login)}
        animal={images.animal}
        back="/"
        slides={slides}
        setDisplaySurprise={setDisplaySurprise}
      />
    );

    setDisplaySurprise(true);
    setKeyIcon(true);
    setPresentIcon(false);
  };

  // ------------------------------------------------------------------------
  // Animation Handlers
  // ------------------------------------------------------------------------

  const keyAnimation = () => {
    setRewardAnimationState('fade-out');
    sleep(1000).then(diggingAnimation);
  };

  const diggingAnimation = () => {
    setPlayDigging(true);
  };

  const closeDay8Video = () => {
    setDisplayIntroVideo(true);
    setDisplayBody(false);
    setShowAnimal(false);
    setIntroVideo(AFTER_CELEBRATE_VIDEO_DAY9);
  };

  // ------------------------------------------------------------------------
  // Drag and Drop Handler
  // ------------------------------------------------------------------------

  const handleDragStop = (rewardId, data) => {
    const position = {
      x: data.x / clientWidth,
      y: data.y / clientHeight,
    };

    const isPrize = rewardId === 1 || rewardId === 2;
    const actionType = isPrize
      ? `MODULE12_SET_PRIZE${rewardId}_POSITION`
      : `MODULE12_SET_REWARD${rewardId}_POSITION`;

    dispatch({ type: actionType, payload: position });
  };

  // ------------------------------------------------------------------------
  // iOS Fallback Effects
  // ------------------------------------------------------------------------

  // iOS audio fallback for voice display
  useEffect(() => {
    if (!displayVoice || !isIOSDevice()) return;

    const timer = setTimeout(() => {
      setAudioReady(false);
      setDisplayVoice(false);
      setShowAnimal(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [displayVoice]);

  // iOS audio fallback for dog celebrate audio
  useEffect(() => {
    if (!playDogCelebrate || !isIOSDevice()) return;

    const timer = setTimeout(() => {
      setPlayDogCelebrate(false);
      if (module12.currentday === SPECIAL_DAYS.DAY_8) {
        closeDay8Video();
      } else {
        navigate(NAVIGATION_PATH);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [playDogCelebrate, module12.currentday, navigate]);

  // iOS audio fallback for coin audio
  useEffect(() => {
    if (!coinAudio || !isIOSDevice()) return;

    const timer = setTimeout(() => {
      dispatch({ type: 'MODULE12_SET_COIN', payload: module12.coins + 1 });
      setCoinAudio(false);
      setCoinAnimationState('bounceIcons');
      setCoinIcon(false);
      playButtonAudio();
    }, 5000);

    return () => clearTimeout(timer);
  }, [coinAudio, dispatch, module12.coins, playButtonAudio]);

  // iOS audio fallback for digging audio
  useEffect(() => {
    if (!playDiggingAudio || !isIOSDevice()) return;

    setAudioReady(false);
    setPlayDigging(false);
    setDirtState(false);
    setPlayDiggingAudio(false);
    setKeyIcon(false);
    setDisplayReward(true);
    setRewardsFound(true);
  }, [playDiggingAudio]);

  // ------------------------------------------------------------------------
  // Audio Event Handlers
  // ------------------------------------------------------------------------

  const handleCoinAudioEnd = () => {
    dispatch({ type: 'MODULE12_SET_COIN', payload: module12.coins + 1 });
    setCoinAudio(false);
    setCoinAnimationState('bounceIcons');
    setCoinIcon(false);
    playButtonAudio();
  };

  const handleVoiceAudioEnd = () => {
    setAudioReady(false);
    setDisplayVoice(false);
    setShowAnimal(false);
  };

  const handleDogCelebrateEnd = () => {
    setPlayDogCelebrate(false);
    if (module12.currentday === SPECIAL_DAYS.DAY_8) {
      closeDay8Video();
    } else {
      navigate(NAVIGATION_PATH);
    }
  };

  const handleDiggingAudioEnd = () => {
    setAudioReady(false);
    setPlayDigging(false);
    setDirtState(false);
    setPlayDiggingAudio(false);
    setKeyIcon(false);
    setDisplayReward(true);
    setRewardsFound(true);
  };

  const handleLogout = () => {
    navigate('/');
    userHasAuthenticated(false);
    localStorage.setItem('token', 'false');
  };

  // ------------------------------------------------------------------------
  // Score Display Helper
  // ------------------------------------------------------------------------

  const getScoreClassName = () => {
    if (module12.coins >= COIN_PRIZE_THRESHOLD) return 'scoretext100';
    if (module12.coins >= 10) return 'scoretext10';
    return 'scoretext1';
  };

  // ------------------------------------------------------------------------
  // Render Helpers
  // ------------------------------------------------------------------------

  const isSpecialDay = module12.currentday === SPECIAL_DAYS.DAY_4 || module12.currentday === SPECIAL_DAYS.DAY_8;
  const progressBarKeys = isSpecialDay ? progressBar5Keys : progressBar4Keys;
  const maxKeys = isSpecialDay ? 5 : 4;

  const shouldShowMainContent =
    (displayBody && !displayLearn && !displayPractice && !displaySingingTime &&
     !displayCelebrate && !displaySurprise && !displayFTB && !displayDay4Practice) ||
    showAnimal;

  const shouldShowActionButtons =
    !showAnimal && !module12.reviewIcon && !coinIcon && !displayPrize && !dropZone;

  // ------------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------------

  return (
    <>
      {/* Audio Elements */}
      {playDogCelebrate && (
        <audio autoPlay onPlay={() => setAudioReady(true)} onEnded={handleDogCelebrateEnd}>
          <source src={animalCloseAudio} type="audio/mp3" />
        </audio>
      )}

      {playDiggingAudio && (
        <audio
          autoPlay
          onPlay={() => { setDirtState(true); setAudioReady(true); }}
          onEnded={handleDiggingAudioEnd}
        >
          <source src={generateImageUrl('media/Module2Videos/dog_digging.mp3')} type="audio/mp3" />
        </audio>
      )}

      {coinAudio && (
        <audio autoPlay onEnded={handleCoinAudioEnd}>
          <source src={generateImageUrl('media/Module5Videos/earn_token.mp3')} type="audio/mp3" />
        </audio>
      )}

      {checkSound && (
        <audio autoPlay onEnded={() => setCheckSound(false)}>
          <source src={generateImageUrl('media/Module1Videos/button_accept_item.mp3')} type="audio/mp3" />
        </audio>
      )}

      {rewardsFound && (
        <audio autoPlay>
          <source src={generateImageUrl('media/Module2Videos/daily_rewards_present.mp3')} />
        </audio>
      )}

      {touchButton && (
        <audio autoPlay onEnded={() => setTouchButton(false)}>
          <source src={generateImageUrl('media/Module1Videos/touch_button.mp3')} type="audio/mp3" />
        </audio>
      )}

      {buttonSound && (
        <audio autoPlay onEnded={() => setButtonSound(false)}>
          <source src={generateImageUrl('media/Module1Videos/button_bouncing.mp3')} type="audio/mp3" />
        </audio>
      )}

      {displayVoice && (
        <audio autoPlay onPlay={() => {if(!playDogCelebrate){setAudioReady(true)}}} onEnded={handleVoiceAudioEnd}>
          <source src={animalVoice} />
        </audio>
      )}

      {/* Activity Screens */}
      {displayIntroVideo && renderIntroduction()}
      {!showAnimal && displayLearn && learnComponent}
      {!showAnimal && displayPractice && (
        <Practice
          setDisplayPractice={setDisplayPractice}
          background={images.practiceBackground}
          stepComplete="MODULE12_SET_stepCompleted"
          module={module12}
          practiceConversation={answerWatch}
          specialMessage={answerSpecialMessage}
          awesomeJob={amazingJobDog}
        />
      )}
      {!showAnimal && displayFTB && (
        <FollowTheBeatModule7
          unit3={true}
          setDisplayFTB={setDisplayFTB}
          awesomeJob={amazingJobDog}
          instruction={ftbInstruction}
          instructionGIF={ftbInstructionGIF}
          song={ftbMusic}
          count={ftbCount}
          tutorial1={ftbTutorial1}
          tutorial2={ftbTutorial2}
          walkthrough={ftbWalkthrough}
          stepComplete="MODULE12_SET_stepCompleted"
          module={module12}
          score="ftb_understand_feelings"
        />
      )}
      {displaySingingTime && singTimeComponent}
      {!showAnimal && displayCelebrate && celebrateComponent}
      {displaySurprise && surpriseVideo}

      {/* Main World Content */}
      {shouldShowMainContent && (
        <div className="understandfeelingsWorldBody" style={{ backgroundImage: `url(${images.background})` }}>
          {/* Navigation Bar */}
          <div className="navigation">
            <img
              className="logoutBtn"
              src={images.logoutBtn}
              alt="Logout Button"
              onClick={togglePopup}
            />
            <img
              className="JourneyBlueImage"
              src={images.journeyMap}
              alt="Navigation blue world"
              onClick={() => navigate(NAVIGATION_PATH)}
            />
          </div>

          {/* Logout Popup */}
          {isPopupOpen && (
            <PopUp
              content={
                <>
                  <b id="title">Are you sure you want to leave?<br /></b>
                  <button id="logoutButton1" className="logoutButton" onClick={handleLogout}>
                    YES
                  </button>
                  <button id="logoutButton2" className="logoutButton" onClick={togglePopup}>
                    NO
                  </button>
                </>
              }
              handleClose={togglePopup}
            />
          )}

          {/* Prize Elements */}
          {prize && (
            <audio autoPlay>
              <source src={presentSound} />
            </audio>
          )}

          {prize && (
            <img
              className="prize"
              src={generateImageUrl('images/Images/prize.png')}
              alt="Prize"
              onClick={() => { setPrize(false); setDisplayPrize(true); }}
            />
          )}

          {prize && (
            <button className="prize-button" onClick={() => { setPrize(false); setDisplayPrize(true); }}>
              <h5>Click Here</h5>
            </button>
          )}

          {displayPrize && renderPrizeSelection()}

          {/* Dog Character */}
          <img className="dogGif" src={dogState} alt="dog" />

          {showAnimal && !audioReady && <img id="loading" src={loadingGif} alt="loading" />}

          {showAnimal && (
            <img
              alt="dog animation"
              style={audioReady ? {} : { display: 'none' }}
              src={animalState}
              className="dogGif"
              onLoad={() => setDisplayVoice(true)}
              onError={() => setShowAnimal(false)}
            />
          )}

          {playDigging && (
            <img
              alt="dog digging"
              style={audioReady ? {} : { display: 'none' }}
              src={dogDig}
              className="dogGif"
              onLoad={() => setPlayDiggingAudio(true)}
            />
          )}

          {dirtState && (
            <img
              className="dirt"
              src={images.dirtCloud}
              alt="dirt cloud"
              id="dirtAnimation"
              style={{
                position: 'absolute',
                zIndex: 2,
                top: '70%',
                left: '50%',
                height: '120px',
                width: '140px',
                marginTop: '90px',
                marginLeft: '-70px',
              }}
            />
          )}

          {/* Score Bar */}
          <div className="scoreBar">
            <img className="scoreKey" src={images.coinBar} alt="coin counter" />
            <h5 className={getScoreClassName()}>{module12.coins}</h5>
            <p className="scoretext">100</p>
          </div>

          {/* Progress Bar */}
          <div className="progressBar3">
            <img
              className="progressKey3"
              src={progressBarKeys[module12.keys]}
              alt="progress"
            />
            <h3 className="progresstext3">{module12.keys}/{maxKeys}</h3>
          </div>

          {/* Action Buttons */}
          {shouldShowActionButtons && (
            <div className="actionBtnDiv">
              {module12.currentday === SPECIAL_DAYS.DAY_4 && module12.stepCompleted === STEPS.NOT_STARTED && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.singingTimeBtn}
                  alt="singing time"
                  onClick={() => { setTouchButton(true); sleep(500).then(() => { displaySinging(); setCheckStep(STEPS.SINGING_TIME); }); }}
                />
              )}

              {module12.currentday !== SPECIAL_DAYS.DAY_4 && module12.stepCompleted === STEPS.NOT_STARTED && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.learnBtn}
                  alt="learn"
                  onClick={() => { setTouchButton(true); sleep(500).then(() => { openLearn(); setCheckStep(STEPS.LEARN); }); }}
                />
              )}

              {module12.stepCompleted === STEPS.LEARN && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.practiceBtn}
                  alt="practice"
                  onClick={() => { touchSound.play(); sleep(500).then(() => { openPractice(); setCheckStep(STEPS.PRACTICE); }); }}
                />
              )}

              {module12.stepCompleted === STEPS.PRACTICE && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.ftbBtn}
                  alt="follow the beat"
                  onClick={() => { setTouchButton(true); sleep(500).then(() => { openFTB(); setCheckStep(STEPS.FOLLOW_THE_BEAT); }); }}
                />
              )}

              {module12.stepCompleted === STEPS.FOLLOW_THE_BEAT && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.celebrateBtn}
                  alt="celebrate"
                  onClick={() => { setTouchButton(true); sleep(500).then(() => { openCelebrate(); setCheckStep(STEPS.CELEBRATE); }); }}
                />
              )}

              {module12.stepCompleted === STEPS.SINGING_TIME && (
                <img
                  className="actionBtn2"
                  id="bounceIcons"
                  src={images.learnBtn}
                  alt="learn"
                  onClick={() => { setTouchButton(true); sleep(500).then(() => { openLearn(); setCheckStep(STEPS.LEARN); }); }}
                />
              )}
            </div>
          )}

          {/* Coin Icon */}
          {!displayVoice && coinIcon && !displayPrize && (
            <div className="actionBtnDiv">
              {!module12.reviewIcon && (
                <img
                  className="chooseReward"
                  id={coinAnimationState}
                  src={images.coinPick}
                  alt="coin"
                  style={{
                    position: 'absolute',
                    top: '50vh',
                    left: '50vw',
                    width: '100px',
                    height: '100px',
                    marginLeft: '-50px',
                    marginTop: '-50px',
                    zIndex: 10,
                  }}
                  onClick={coinAnimation}
                />
              )}
            </div>
          )}

          {/* Review and Reward Buttons */}
          {!displayPrize && !showAnimal && !displayReward && module12.reviewIcon && (
            <div className="actionBtnDiv">
              {module12.reviewIcon && (
                <img
                  className={showReward ? 'actionBtn3' : 'actionBtn2'}
                  src={images.reviewBtn}
                  alt="review"
                  onClick={() => { setTouchButton(true); sleep(500).then(() => setReviewIconVisible(!reviewIconVisible)); }}
                />
              )}

              {showReward && (
                <img
                  src={images.rewardBtn}
                  className="actionBtn4"
                  alt="reward"
                  onClick={() => { setTouchButton(true); sleep(500).then(keyAnimation); }}
                />
              )}

              {module12.reviewIcon && reviewIconVisible && renderReviewButtons()}
            </div>
          )}

          {/* Key Icon */}
          {keyIcon && !coinIcon && !displayPrize && (
            <img
              className="rewardsIcon"
              style={{ width: '150px', height: '50px', marginLeft: '-75px', zIndex: 10 }}
              id={rewardAnimationState}
              src={images.keyReward}
              alt="reward bone"
              onClick={keyAnimation}
            />
          )}

          {displayReward && renderRewardSelection()}

          {/* Present Icon */}
          {!displayPrize && !coinIcon && presentIcon && (
            <>
              <img
                className="present"
                style={{ width: '84px', height: '100px', marginLeft: '-42px', zIndex: 10 }}
                id="bounceIcons"
                src={images.present}
                alt="gift"
                onClick={playSurpriseVideo}
              />
              <audio autoPlay>
                <source src={presentSound} />
              </audio>
            </>
          )}

          {/* Draggable Rewards Area */}
          <div className="rewardArea">
            {/* Day 1 Rewards: Slides */}
            {module12.rewards.SlideRed.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.SlideRed.x * clientWidth,
                  y: module12.rewards.SlideRed.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(11, data)}
              >
                <div className="moveSlide1" style={{ backgroundImage: `url(${images.rewards.SlideRed})` }} />
              </Draggable>
            )}

            {module12.rewards.SlideYellow.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.SlideYellow.x * clientWidth,
                  y: module12.rewards.SlideYellow.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(12, data)}
              >
                <div className="moveSlide2" style={{ backgroundImage: `url(${images.rewards.SlideYellow})` }} />
              </Draggable>
            )}

            {/* Day 2 Rewards: Gnomes */}
            {module12.rewards.GnomeBlue.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.GnomeBlue.x * clientWidth,
                  y: module12.rewards.GnomeBlue.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(21, data)}
              >
                <div className="moveSmallReward1" style={{ backgroundImage: `url(${images.rewards.GnomeBlue})` }} />
              </Draggable>
            )}

            {module12.rewards.GnomeGreen.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.GnomeGreen.x * clientWidth,
                  y: module12.rewards.GnomeGreen.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(22, data)}
              >
                <div className="moveSmallReward2" style={{ backgroundImage: `url(${images.rewards.GnomeGreen})` }} />
              </Draggable>
            )}

            {/* Day 3 Rewards: Playground Equipment */}
            {module12.rewards.SpringToy.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.SpringToy.x * clientWidth,
                  y: module12.rewards.SpringToy.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(31, data)}
              >
                <div className="moveReward1" style={{ backgroundImage: `url(${images.rewards.SpringToy})` }} />
              </Draggable>
            )}

            {module12.rewards.TireSwing.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.TireSwing.x * clientWidth,
                  y: module12.rewards.TireSwing.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(32, data)}
              >
                <div className="moveTire2" style={{ backgroundImage: `url(${images.rewards.TireSwing})` }} />
              </Draggable>
            )}

            {/* Day 4 Rewards: Fairies */}
            {module12.rewards.FairyGreen.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.FairyGreen.x * clientWidth,
                  y: module12.rewards.FairyGreen.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(41, data)}
              >
                <div className="moveClam1" style={{ backgroundImage: `url(${images.rewards.FairyGreen})` }} />
              </Draggable>
            )}

            {module12.rewards.FairyPurple.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.FairyPurple.x * clientWidth,
                  y: module12.rewards.FairyPurple.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(42, data)}
              >
                <div className="moveClam2" style={{ backgroundImage: `url(${images.rewards.FairyPurple})` }} />
              </Draggable>
            )}

            {/* Day 5 Rewards: Monkey Bars */}
            {module12.rewards.MonkeybarsBlue.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.MonkeybarsBlue.x * clientWidth,
                  y: module12.rewards.MonkeybarsBlue.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(51, data)}
              >
                <div className="moveSurfBoard1" style={{ backgroundImage: `url(${images.rewards.MonkeybarsBlue})` }} />
              </Draggable>
            )}

            {module12.rewards.MonkeybarsYellow.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.MonkeybarsYellow.x * clientWidth,
                  y: module12.rewards.MonkeybarsYellow.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(52, data)}
              >
                <div className="moveSurfBoard2" style={{ backgroundImage: `url(${images.rewards.MonkeybarsYellow})` }} />
              </Draggable>
            )}

            {/* Day 6 Rewards: Frogs */}
            {module12.rewards.FrogCrown.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.FrogCrown.x * clientWidth,
                  y: module12.rewards.FrogCrown.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(61, data)}
              >
                <div className="moveClam1" style={{ backgroundImage: `url(${images.rewards.FrogCrown})` }} />
              </Draggable>
            )}

            {module12.rewards.FrogVelvet.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.FrogVelvet.x * clientWidth,
                  y: module12.rewards.FrogVelvet.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(62, data)}
              >
                <div className="moveClam2" style={{ backgroundImage: `url(${images.rewards.FrogVelvet})` }} />
              </Draggable>
            )}

            {/* Day 7 Rewards: Kites */}
            {module12.rewards.KitePink.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.KitePink.x * clientWidth,
                  y: module12.rewards.KitePink.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(71, data)}
              >
                <div className="moveReward1" style={{ backgroundImage: `url(${images.rewards.KitePink})` }} />
              </Draggable>
            )}

            {module12.rewards.KitePurple.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.KitePurple.x * clientWidth,
                  y: module12.rewards.KitePurple.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(72, data)}
              >
                <div className="moveReward2" style={{ backgroundImage: `url(${images.rewards.KitePurple})` }} />
              </Draggable>
            )}

            {/* Day 8 Rewards: Flowers */}
            {module12.rewards.FlowerPink.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.FlowerPink.x * clientWidth,
                  y: module12.rewards.FlowerPink.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(81, data)}
              >
                <div className="moveReward1" style={{ backgroundImage: `url(${images.rewards.FlowerPink})` }} />
              </Draggable>
            )}

            {module12.rewards.FlowerBlue.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.FlowerBlue.x * clientWidth,
                  y: module12.rewards.FlowerBlue.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(82, data)}
              >
                <div className="moveReward2" style={{ backgroundImage: `url(${images.rewards.FlowerBlue})` }} />
              </Draggable>
            )}

            {/* Prize Rewards: Fae Dragon and House */}
            {module12.rewards.FaeDragon.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.FaeDragon.x * clientWidth,
                  y: module12.rewards.FaeDragon.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(1, data)}
              >
                <div className="movePrize12" style={{ backgroundImage: `url(${images.rewards.FaeDragon})` }} />
              </Draggable>
            )}

            {module12.rewards.FaeHouse.unlocked && (
              <Draggable
                disabled={!displayGreenButton}
                bounds="parent"
                position={{
                  x: module12.rewards.FaeHouse.x * clientWidth,
                  y: module12.rewards.FaeHouse.y * clientHeight,
                }}
                onStop={(e, data) => handleDragStop(2, data)}
              >
                <div className="movePrize22" style={{ backgroundImage: `url(${images.rewards.FaeHouse})` }} />
              </Draggable>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Module12World;
